import { Grid, Box, makeStyles, Skeleton, LottiePlayerECOMMBX, Container } from '@dxb-web-ui-framework/design-system';
import clsx from 'clsx';
import React, { VFC, useEffect } from 'react';
import { DataServiceHookResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { formatAmount, isEmpty } from '@dxb-web-ui-framework/common/utils';
import { useTranslation } from 'react-i18next';
import { useScreenDetector } from '@dxb-web-ui-framework/common/hooks';
import { Nullable } from '@dxb-web-ui-framework/common/types';
import { isDefined } from '@dxb-web-ui-framework/common/utils/Helper';
import { SkeletonTypography } from '../../../../../content-composer/src/components/Generic/Skeleton';
import { useLanguage } from '../../../../../internationalization/src/contexts/Internationalization/hooks';
import { ErrorContent } from '../../../../../content-composer/src/components/Partials/ErrorContent';
import { useWizardStepData } from '../../../../../content-composer/src/contexts/ClientSideWizard/hooks';
import { ExecuteMandateResponseBody } from '../../../domain/Authorization/entities/ExecuteMandateResponseDTO';
import {
  useAuthorizationPayrollDetails,
  useAuthorizationPayrollLoadingState,
  useAuthorizationPayrollErrorState,
  useIsPayrollAuthFlowDrawerOpen,
  useOnRefetchPayrollDetailsMutation,
  useAuthorizationSelectedPayroll,
} from '../../../contexts/Payroll/hooks';
import { useOnRefetchPendingAuthizationsListMutation } from '../../../contexts';
import { usePayrollService } from './usePayrollService';

const useStyles = makeStyles(theme => ({
  root: {
    marginTop: theme.spacing(2),
  },
  container: {
    gap: theme.spacing(2),
  },
  successText: {
    color: theme.palette.success.main,
  },
  text: {
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  rejected: {
    display: 'inline-block',
    color: theme.palette.error.main,
  },
  accepted: {
    display: 'inline-block',
    color: theme.palette.success.main,
  },
  lottieContainer: {
    maxHeight: '520px',
    width: '100%',
    height: '100%',
    [theme.breakpoints.down('sm')]: {
      margin: 'auto',
    },
  },
}));

export interface MandateExecuteStepProps {
  authAction: string | null;
}

const PayrollExecuteStep: VFC<MandateExecuteStepProps> = ({ authAction }) => {
  const { t } = useTranslation();
  const { isExtraSmallScreen: isMobile } = useScreenDetector();
  const language = useLanguage();
  const previousStepData = useWizardStepData('payrollInitiate') as {
    values: { requestId?: string };
  };
  // drawer state
  const isPayrollAuthFlowDrawerOpen = useIsPayrollAuthFlowDrawerOpen();
  // loading state
  const isLoadingPayrollDetails = useAuthorizationPayrollLoadingState('isLoadingPayrollDetails');
  const isLoadingCheckAuthorization = useAuthorizationPayrollLoadingState('isLoadingCheckAuthorization');

  // error state
  const payrollDetailsError = useAuthorizationPayrollErrorState('payrollDetailsError');

  // mutations
  const refetchPayrollDetailsMutation = useOnRefetchPayrollDetailsMutation();
  const refetchPayrollListMutation = useOnRefetchPendingAuthizationsListMutation();

  // provider data
  const selectedAuthorizationPayrollDetails = useAuthorizationPayrollDetails();
  const selectedPayroll = useAuthorizationSelectedPayroll();

  // Execute Authorization
  const {
    data: executeMandateData,
    isLoading: isLoadingExecuteAuthorization,
    error: executeAuthorizationError,
    mutate: onExecuteAuthorizationMutation,
  } = (
    usePayrollService('execute') as (
      successCallback: () => void,
      requestId?: Nullable<string>,
    ) => DataServiceHookResponse<ExecuteMandateResponseBody>
  )(() => {}, previousStepData.values?.requestId);

  const globalOrderId = selectedPayroll?.globalOrderId;
  const isLoading = isLoadingPayrollDetails || isLoadingCheckAuthorization || isLoadingExecuteAuthorization;
  const error = payrollDetailsError || executeAuthorizationError;

  useEffect(() => {
    if (executeMandateData) {
      refetchPayrollListMutation();
    }
  }, [isPayrollAuthFlowDrawerOpen, refetchPayrollListMutation, executeMandateData]);

  const mutatorHandler = () => {
    if (payrollDetailsError) {
      refetchPayrollDetailsMutation();
      return;
    }

    onExecuteAuthorizationMutation && onExecuteAuthorizationMutation();
  };

  const successTitleRenderer = () => {
    if (executeMandateData?.transferExecuted === true) {
      if (globalOrderId) {
        return `${t('authorizationsListPage.ApproveRejectFlow.isLastAuthorizer.Success.Subtitle')} ${globalOrderId}`;
      } else {
        return t('authorizationsListPage.ApproveRejectFlow.Success.Accepted.Subtitle');
      }
    } else {
      return t('authorizationsListPage.ApproveRejectFlow.isNotLastAuthorizer.Success.Subtitle');
    }
  };

  const classes = useStyles();

  return (
    <Box display='flex' flexDirection='column' width='100%' height='100%' alignItems='center' justifyContent='center'>
      {error && !isLoading && (
        <ErrorContent
          code={error?.code || '404'}
          onClickBtn={mutatorHandler}
          subtitle={error?.message || t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Execute.Subtitle')}
          btnLabel={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.BtnLabel')}
          message={t('authorizationsListPage.ApproveRejectFlow.ErrorContent.Message')}
        />
      )}
      {!error && isEmpty(error) && (
        <Container maxWidth='md' className={classes.root}>
          <Grid container className={classes.container} justifyContent='center'>
            <Grid container item xs={12} md={3} justifyContent='center' direction='column'>
              <SkeletonTypography
                hasSkeleton={isLoading}
                skeletonProps={{ variant: 'text', width: '100%' }}
                typographyProps={{
                  variant: isMobile ? 'h3' : 'h2',
                  component: 'h1',
                  className: clsx(classes.successText, classes.text),
                }}>
                {t('fundsTransfer.SuccessStep.label')}
              </SkeletonTypography>
              <Box mt={2} mb={2}>
                {authAction === 'approve' && (
                  <SkeletonTypography
                    skeletonProps={{ variant: 'text', width: '100%' }}
                    hasSkeleton={isLoading}
                    typographyProps={{ variant: 'h5', color: 'secondary', className: classes.text }}>
                    {successTitleRenderer()}
                  </SkeletonTypography>
                )}
                {authAction === 'reject' && (
                  <SkeletonTypography
                    skeletonProps={{ variant: 'text', width: '100%' }}
                    hasSkeleton={isLoading}
                    typographyProps={{ variant: 'h5', color: 'secondary', className: classes.text }}>
                    {t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Payroll.Subtitle1')}{' '}
                    <span className={classes.rejected}>
                      {t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Payroll.Subtitle2')}
                    </span>{' '}
                    {t('authorizationsListPage.ApproveRejectFlow.Success.Reject.Payroll.Subtitle3')}
                  </SkeletonTypography>
                )}
              </Box>
              {isDefined(globalOrderId) && (
                <SkeletonTypography
                  skeletonProps={{ variant: 'text', width: '100%' }}
                  hasSkeleton={isLoading}
                  typographyProps={{ color: 'textSecondary', className: classes.text }}>
                  {selectedAuthorizationPayrollDetails?.totalAmount && selectedPayroll
                    ? formatAmount(
                        Number(selectedAuthorizationPayrollDetails?.totalAmount),
                        selectedPayroll?.currency,
                        2,
                        language.cultureName,
                      )
                    : '-'}
                </SkeletonTypography>
              )}
            </Grid>
            <Grid item xs={12} md={8}>
              <Box className={classes.lottieContainer} mt={isLoading ? 2 : 0} flex={1}>
                {isLoading ? (
                  <Skeleton width='100%' height='100%'>
                    <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
                  </Skeleton>
                ) : (
                  <LottiePlayerECOMMBX icon='ecommbx_success' loop play />
                )}
              </Box>
            </Grid>
          </Grid>
        </Container>
      )}
    </Box>
  );
};

export { PayrollExecuteStep };
